import React from 'react';
import { Ban, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { deactivateFacilityOptions, reactivateFacilityOptions } from '@generated';
import { createQueryDataTable, type PropsOfQueryDataTable, type RowOfQueryDataTable } from '~/shared/data-tables/query/QueryDataTable';
import { Prompt } from '~/shared/components/Prompt';
import { RenderAddressCell } from '~/shared/data-tables/column-types/AddressColumn';
import { ActiveColumnCell } from '~/shared/data-tables/column-types/ActiveColumn';
import { useMutation } from '~/util/http/useMutation';
import { getSearchQueryKey } from '~/util/http/useSearch';
import { Button } from '~/shared/components/buttons/Button';
import { TableActions } from '~/shared/data-tables/TableActions';

const FacilitiesDataTable = createQueryDataTable('facility');
type RowType = RowOfQueryDataTable<typeof FacilitiesDataTable>;
type TableProps = PropsOfQueryDataTable<typeof FacilitiesDataTable>;

function ActionsCell({ row }: { row: RowType }) {
	const navigate = useNavigate();

	const deactivateFacility = useMutation({
		...deactivateFacilityOptions,
		invalidateQueryKeys: [getSearchQueryKey('facility')]
	});

	const reactivateFacility = useMutation({
		...reactivateFacilityOptions,
		invalidateQueryKeys: [getSearchQueryKey('facility')]
	});

	return (
		<TableActions>
			{row.active ? (
				<>
					<TableActions.ViewButton aria-label="Edit Facility" onClick={() => navigate(`edit/${row.id}`)} />
					<Button
						Icon={Ban}
						aria-label="Deactivate Facility"
						intent="destructive"
						appearance="ghost"
						renderModal={closeModal => (
							<Prompt
								title="Deactivate Facility"
								text="Are you sure you want to deactivate this facility?"
								onAction={() => {
									deactivateFacility.mutateAsync({ facilityId: row.id });
									closeModal();
								}}
								onCancel={closeModal}
							/>
						)}
					/>
				</>
			) : (
				<Button
					Icon={Eye}
					aria-label="Reactivate Facility"
					appearance="ghost"
					renderModal={closeModal => (
						<Prompt
							title="Reactivate Facility"
							text="Are you sure you want to reactivate this facility?"
							onAction={() => {
								reactivateFacility.mutateAsync({ facilityId: row.id });
								closeModal();
							}}
							onCancel={closeModal}
						/>
					)}
				/>
			)}
		</TableActions>
	);
}

function FacilityNameCell({ row }: { row: RowType }) {
	return (
		<div className="flex flex-col">
			<div className="flex items-center gap-2">
				<span className="text-sm font-semibold text-gray-900">{row.name}</span>
			</div>
			{row.notes && <p className="mt-1 max-w-xs truncate text-xs text-gray-500">{row.notes}</p>}
		</div>
	);
}

const columnDefinitions = FacilitiesDataTable.columns([
	{
		header: 'Facility Name',
		key: 'name',
		sortable: true,
		width: 'w-[300px]',
		renderCell: row => <FacilityNameCell row={row} />
	},
	{
		header: 'Type',
		key: 'facilityTypeLabel',
		sortable: true
	},
	{
		header: 'Address',
		key: 'city',
		width: 'w-[200px]',
		renderCell: row => (
			<RenderAddressCell address={{ addressOne: '', city: row.city ?? '', state: row.state ?? '', zipCode: row.zipCode ?? '' }} />
		)
	},
	{
		header: 'Phone Number',
		key: 'phoneNumber',
		type: 'phone'
	},
	{
		header: 'Fax Number',
		key: 'faxNumber',
		type: 'phone'
	},
	{
		header: 'Facility Hours',
		key: 'hoursOfOperation'
	},
	{
		header: 'Active',
		key: 'active',
		width: 'w-[100px]',
		renderCell: row => <ActiveColumnCell active={row.active} />
	},
	{
		header: 'Actions',
		key: 'actions',
		width: 'w-[100px]',
		renderCell: row => <ActionsCell row={row} />
	}
]);

export function FacilityTable(props: TableProps) {
	return (
		<FacilitiesDataTable.Table
			columns={columnDefinitions}
			emptyMessage="No facilities found."
			getMuteRowOpacity={row => !row.active}
			{...props}
		/>
	);
}

export function FacilitiesTable(props: TableProps) {
	return <FacilitiesDataTable.Table columns={columnDefinitions} emptyMessage="No facilities found." {...props} />;
}
